import React, { useEffect, useState } from "react"
import { Button } from "@mui/material"
import { useLocation, useNavigate } from "react-router-dom"
import { useMutation } from "react-query"
import Swal from "sweetalert2"
import { LocationState } from "../../api/models/cvskill"
import cvskillService from "../../api/services/cvskillService"

interface CheckedItems {
	[key: string]: boolean
}

interface TypePersonnalite {
	id: number
	personnaliteType: string
	associatedTraits: string[]
}

function PolePersonnalite2() {
	const [items, setItems] = useState<TypePersonnalite[]>([])
	const [checkedItems, setCheckedItems] = useState<CheckedItems>({})
	const [error, setError] = useState<string | null>(null)
	const navigate = useNavigate()
	const location = useLocation()
	const state = location.state as LocationState
	const [userId, setUserId] = useState<number | null>(null)
	const [cvSkillId, setCvSkillId] = useState<number | null>(null)
	const { editMode } = state || {}

	// 1️⃣ Charger la liste des types de personnalité depuis le backend
	useEffect(() => {
		const loadTypes = async () => {
			try {
				console.log(
					"[LOG FRONT HUGO] 🔄 Chargement des types de personnalité...",
				)
				const typesFromBackend =
					await cvskillService.getPersonnaliteTypes()
				console.log(
					"[LOG FRONT HUGO] ✅ Types récupérés :",
					typesFromBackend,
				)

				// Transformation pour correspondre à TypePersonnalite
				const types: TypePersonnalite[] = typesFromBackend.map(
					(t: any) => ({
						id: t.id,
						personnaliteType: t.personnaliteType,
						associatedTraits: t.associatedTraits || [], // important pour TypeScript
					}),
				)

				setItems(types)
			} catch (err) {
				console.error(
					"[LOG FRONT HUGO] ❌ Erreur chargement types :",
					err,
				)
				setError("Impossible de charger les types de personnalité.")
			}
		}
		loadTypes()
	}, [])

	// 2️⃣ Charger userId, cvSkillId ET précocher en mode édition
	useEffect(() => {
		const fetchData = async () => {
			try {
				const storedUserId = localStorage.getItem("selectedUserId")
				const storedCvSkillId =
					localStorage.getItem("selectedCvSkillId")

				console.log(
					"[LOG FRONT HUGO] 📋 Données localStorage - userId:",
					storedUserId,
					"cvSkillId:",
					storedCvSkillId,
					"editMode:",
					editMode,
				)

				if (storedUserId) setUserId(Number(storedUserId))
				if (storedCvSkillId) setCvSkillId(Number(storedCvSkillId))

				// Mode édition → charger et précocher le type déjà sélectionné
				if (editMode && storedCvSkillId) {
					console.log(
						"[LOG FRONT HUGO] 🔄 Mode édition - Chargement du CvSkill...",
					)
					const cvSkill = await cvskillService.getCvSkillById(
						Number(storedCvSkillId),
					)
					console.log(
						"[LOG FRONT HUGO] 📦 CvSkill récupéré:",
						cvSkill,
					)

					if (
						cvSkill?.polePersonnalitesTypes &&
						cvSkill.polePersonnalitesTypes.length > 0
					) {
						const existingType = cvSkill.polePersonnalitesTypes[0]
						const checked: CheckedItems = {}
						checked[String(existingType.id)] = true
						setCheckedItems(checked)
						console.log(
							"[LOG FRONT HUGO] ✅ Type précoché en mode édition:",
							existingType,
						)
					} else {
						console.log(
							"[LOG FRONT HUGO] ⚠️ Aucun type de personnalité trouvé dans le CvSkill",
						)
					}
				} else if (!editMode && storedUserId) {
					// Mode création → charger depuis localStorage si disponible
					const storedType = localStorage.getItem(
						`selectedPersonalityType-${storedUserId}`,
					)
					if (storedType) {
						try {
							const parsed = JSON.parse(storedType)
							if (parsed && parsed.id) {
								const checked: CheckedItems = {}
								checked[String(parsed.id)] = true
								setCheckedItems(checked)
								console.log(
									"[LOG FRONT HUGO] ✅ Type rechargé depuis localStorage:",
									parsed,
								)
							}
						} catch (e) {
							console.warn(
								"[LOG FRONT HUGO] ⚠️ Erreur parsing localStorage type:",
								e,
							)
						}
					}
				}
			} catch (error) {
				console.error(
					"[LOG FRONT HUGO] ❌ Erreur chargement données:",
					error,
				)
				setError(
					"Impossible de récupérer les données. Veuillez réessayer.",
				)
			}
		}

		fetchData()
	}, [editMode])

	// 3️⃣ Gérer la sélection (un seul choix autorisé)
	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const { name, checked } = event.target
		const updatedCheckedItems: CheckedItems = {}
		if (checked) updatedCheckedItems[name] = true
		setCheckedItems(updatedCheckedItems)
		setError(null)

		if (!editMode && userId) {
			const selectedType = items.find(item => item.id.toString() === name)
			if (selectedType) {
				localStorage.setItem(
					`selectedPersonalityType-${userId}`,
					JSON.stringify({
						id: selectedType.id,
						personnaliteType: selectedType.personnaliteType,
					}),
				)
				console.log("[LOG FRONT HUGO] 💾 Type stocké :", {
					id: selectedType.id,
					personnaliteType: selectedType.personnaliteType,
				})
			}
			localStorage.setItem(
				`selectedAssociatedTraits-${userId}`,
				JSON.stringify([]),
			)
		}
	}

	// 4️⃣ Mutation pour la mise à jour
	const updateCvSkillMutation = useMutation(
		(updatedCvSkill: any) => {
			if (!cvSkillId || !userId)
				throw new Error("CV Skill ID ou User ID invalide")
			return cvskillService.updateCvSkill(
				cvSkillId,
				updatedCvSkill,
				userId,
			)
		},
		{
			onSuccess: () => {
				Swal.fire({
					icon: "success",
					title: "Personnalité mise à jour avec succès",
					showConfirmButton: false,
					timer: 1500,
				})
				navigate("/cvskill/cv", { state: { userId, cvSkillId } })
			},
			onError: error => {
				console.error("[LOG FRONT HUGO] ❌ Erreur mutation:", error)
				setError("Erreur lors de la mise à jour.")
			},
		},
	)

	// 5️⃣ Soumission
	const handleSubmit = async () => {
		const selectedId = Object.keys(checkedItems).find(
			key => checkedItems[key],
		)
		if (!selectedId) {
			setError("Veuillez sélectionner un type de personnalité.")
			return
		}

		const selectedType = items.find(
			item => item.id.toString() === selectedId,
		)
		if (!selectedType) return

		if (editMode && cvSkillId && userId) {
			try {
				const currentCvSkill =
					await cvskillService.getCvSkillById(cvSkillId)

				const traitsToUpdate =
					state?.polePersonnaliteTraits ||
					currentCvSkill.polePersonnaliteTraits ||
					[]

				console.log(
					"[LOG FRONT HUGO] 📤 Mise à jour en mode édition avec:",
					{
						traits: traitsToUpdate,
						type: selectedType,
						associatedTraits: selectedType.associatedTraits,
					},
				)

				updateCvSkillMutation.mutate({
					...currentCvSkill,
					polePersonnaliteTraits: traitsToUpdate,
					polePersonnalitesTypes: [
						{
							id: selectedType.id,
							personnaliteType: selectedType.personnaliteType,
							associatedTraits: selectedType.associatedTraits,
						},
					],
				})
			} catch (err) {
				console.error("[LOG FRONT HUGO] ❌ Erreur mise à jour:", err)
				setError("Erreur lors de la mise à jour.")
			}
		} else {
			console.log(
				"[LOG FRONT HUGO] 🚀 Navigation vers Pôle Atouts avec :",
				{
					userId,
					cvSkillId,
					selectedType,
				},
			)
			navigate("/cvskill/PoleAtouts", {
				state: {
					...state,
					userId,
					cvSkillId,
					selectedType,
				},
			})
		}
	}

	const selectedCount = Object.values(checkedItems).filter(Boolean).length

	return (
		<div className="container mx-auto p-4 max-w-4xl relative overflow-y-auto h-[calc(100vh-8rem)] pb-16">
			<style>{`
                .container::-webkit-scrollbar {
                    display: none;
                }
                .container {
                    scrollbar-width: none;
                    -ms-overflow-style: none;
                }
                
                .page-header {
                    background: linear-gradient(135deg, #2daf8e 0%, #238f74 100%);
                    color: white;
                    padding: 20px 24px;
                    border-radius: 16px;
                    text-align: center;
                    box-shadow: 0 4px 12px rgba(45, 175, 142, 0.3);
                    margin-bottom: 24px;
                }
                
                .page-header h2 {
                    font-size: 24px;
                    font-weight: 700;
                    margin: 0;
                }
                
                .part-badge {
                    display: inline-block;
                    background: white;
                    color: #2daf8e;
                    padding: 8px 16px;
                    border-radius: 20px;
                    font-weight: 600;
                    font-size: 14px;
                    margin-top: 12px;
                }
                
                .counter-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    background: ${selectedCount === 1 ? "#2daf8e" : "#f3f4f6"};
                    color: ${selectedCount === 1 ? "white" : "#6b7280"};
                    padding: 12px 20px;
                    border-radius: 12px;
                    font-weight: 600;
                    font-size: 16px;
                    box-shadow: ${selectedCount === 1 ? "0 4px 12px rgba(45, 175, 142, 0.3)" : "0 1px 3px rgba(0, 0, 0, 0.1)"};
                    margin-bottom: 20px;
                    transition: all 0.3s ease;
                }
                
                .selection-card {
                    position: relative;
                    background: white;
                    border: 2px solid #e5e7eb;
                    border-radius: 12px;
                    padding: 16px;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
                }
                
                .selection-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 12px rgba(45, 175, 142, 0.2);
                    border-color: #7dd4c1;
                }
                
                .selection-card.selected {
                    background: linear-gradient(135deg, #d4f5ed 0%, #b8ede0 100%);
                    border-color: #2daf8e;
                    box-shadow: 0 4px 12px rgba(45, 175, 142, 0.3);
                }
                
                .selection-checkbox {
                    appearance: none;
                    width: 24px;
                    height: 24px;
                    border: 2px solid #d1d5db;
                    border-radius: 6px;
                    cursor: pointer;
                    position: relative;
                    transition: all 0.2s ease;
                    flex-shrink: 0;
                }
                
                .selection-checkbox:hover {
                    border-color: #2daf8e;
                }
                
                .selection-checkbox:checked {
                    background-color: #2daf8e;
                    border-color: #2daf8e;
                }
                
                .selection-checkbox:checked::after {
                    content: "✓";
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    color: white;
                    font-size: 16px;
                    font-weight: bold;
                }
                
                .trait-tag {
                    display: inline-block;
                    background: linear-gradient(135deg, #f0fdf9 0%, #e6f7f3 100%);
                    color: #047857;
                    padding: 6px 12px;
                    border-radius: 8px;
                    font-size: 13px;
                    font-weight: 500;
                    margin: 4px 4px 4px 0;
                    border: 1px solid #a7f3d0;
                    transition: all 0.2s ease;
                }
                
                .selected .trait-tag {
                    background: linear-gradient(135deg, #2daf8e 0%, #238f74 100%);
                    color: white;
                    border-color: #238f74;
                    font-weight: 600;
                }
                
                .trait-tag:hover {
                    transform: scale(1.05);
                }
                
                .traits-container {
                    margin-top: 12px;
                    padding-top: 12px;
                    border-top: 1px solid #e5e7eb;
                }
                
                .selected .traits-container {
                    border-top-color: rgba(45, 175, 142, 0.3);
                }
                
                .traits-label {
                    font-size: 12px;
                    color: #6b7280;
                    font-weight: 600;
                    margin-bottom: 8px;
                    display: block;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }
                
                .selected .traits-label {
                    color: #047857;
                }
                
                .action-button {
                    background: linear-gradient(135deg, #2daf8e 0%, #238f74 100%) !important;
                    box-shadow: 0 4px 12px rgba(45, 175, 142, 0.3) !important;
                    text-transform: none !important;
                    font-size: 16px !important;
                    font-weight: 600 !important;
                    padding: 12px 24px !important;
                    border-radius: 12px !important;
                    width: 100%;
                    transition: all 0.3s ease !important;
                }
                
                .action-button:hover:not(:disabled) {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 16px rgba(45, 175, 142, 0.4) !important;
                }
                
                .action-button:disabled {
                    background: #d1d5db !important;
                    box-shadow: none !important;
                    cursor: not-allowed;
                }
            `}</style>

			<div className="page-header">
				<h2>
					{editMode
						? "Modifier le type de personnalité"
						: "Pôle Personnalité"}
				</h2>
				<div className="part-badge">Partie 2 sur 2</div>
			</div>

			{error && (
				<p className="text-red-500 text-center bg-red-50 p-3 rounded-lg mb-4">
					⚠️ {error}
				</p>
			)}

			<div className="counter-badge">
				{selectedCount === 1 ? "✓" : "📊"} {selectedCount} / 1 type
				sélectionné
			</div>

			{items.length === 0 ? (
				<div className="text-center py-8">
					<p className="text-gray-500">
						Chargement des types de personnalité...
					</p>
				</div>
			) : (
				<div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
					{items.map(item => {
						const isSelected =
							checkedItems[item.id.toString()] || false
						return (
							<div
								key={item.id}
								className={`selection-card ${isSelected ? "selected" : ""}`}
								onClick={() =>
									handleChange({
										target: {
											name: item.id.toString(),
											checked: !isSelected,
										},
									} as React.ChangeEvent<HTMLInputElement>)
								}
							>
								<label
									htmlFor={item.id.toString()}
									className="cursor-pointer"
								>
									<div className="flex items-center space-x-3">
										<input
											type="checkbox"
											id={item.id.toString()}
											name={item.id.toString()}
											checked={isSelected}
											onChange={() => {}}
											className="selection-checkbox"
										/>
										<span className="text-gray-800 font-medium text-base flex-1">
											{item.personnaliteType}
										</span>
									</div>

									{item.associatedTraits &&
										item.associatedTraits.length > 0 && (
											<div className="traits-container">
												<span className="traits-label">
													Traits associés
												</span>
												<div className="flex flex-wrap">
													{item.associatedTraits.map(
														trait => (
															<span
																key={trait}
																className="trait-tag"
															>
																{trait}
															</span>
														),
													)}
												</div>
											</div>
										)}
								</label>
							</div>
						)
					})}
				</div>
			)}

			<p className="mt-6 font-bold text-center text-red-500 bg-red-50 p-3 rounded-lg">
				⚠️ 1 choix obligatoire
			</p>

			<div className="flex justify-center mt-8">
				<Button
					onClick={handleSubmit}
					variant="contained"
					className="action-button"
					disabled={selectedCount !== 1}
				>
					{editMode ? "Mettre à jour" : "Continuer →"}
				</Button>
			</div>
		</div>
	)
}

export default PolePersonnalite2
